import { useEffect, useRef } from 'react';
import { StabeonEvent } from '../types/event';
import { StabeonUser } from '../types/user';
import { useNotifications } from './useNotifications';
import { usePushNotify } from './usePushNotify';
import { useToast } from './useToast';
import gun from '../config/gun.config';

const REMINDER_BEFORE_MS = 60 * 60 * 1000; // 1 hour before
const CHECK_INTERVAL_MS = 60 * 1000;
const STORAGE_KEY = 'joystance_reminded_events';

function getRemindedIds(): Set<string> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? new Set(JSON.parse(saved)) : new Set();
  } catch { return new Set(); }
}

function saveRemindedId(id: string) {
  const ids = getRemindedIds();
  ids.add(id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
}

function distKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dlat = lat1 - lat2;
  const dlng = lng1 - lng2;
  return Math.sqrt(dlat * dlat + dlng * dlng) * 111;
}

export function useEventReminders(
  events: StabeonEvent[],
  myId: string | null,
  allUsers?: StabeonUser[],
  eventsNotifEnabled?: boolean,
) {
  const { notify } = useNotifications();
  const { push } = usePushNotify();
  const { show } = useToast();
  const checkedRef = useRef(false);
  // Keep allUsers in a ref so the effect doesn't re-run every tick
  const allUsersRef = useRef(allUsers);
  useEffect(() => { allUsersRef.current = allUsers; }, [allUsers]);
  // Keep notif flag in a ref for the same reason
  const notifEnabledRef = useRef(eventsNotifEnabled !== false);
  useEffect(() => { notifEnabledRef.current = eventsNotifEnabled !== false; }, [eventsNotifEnabled]);

  useEffect(() => {
    if (!myId || events.length === 0) return;

    const check = () => {
      const now = Date.now();
      const reminded = getRemindedIds();

      events.forEach(event => {
        if (reminded.has(event.id)) return;
        const timeUntil = event.startsAt - now;

        // 1-hour reminder
        if (timeUntil > 0 && timeUntil <= REMINDER_BEFORE_MS) {
          const mins = Math.round(timeUntil / 60000);
          const title = `"${event.title}" in ${mins}m`;
          const message = `Starting at ${new Date(event.startsAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
          if (notifEnabledRef.current) {
            notify('Joystance', title);
            show(title, 'info');
            push([myId], title, message);
          }
          saveRemindedId(event.id);
        }

        // Starting now
        if (timeUntil <= 0 && timeUntil > -60000) {
          const title = `"${event.title}" is starting now!`;
          if (notifEnabledRef.current) {
            notify('Joystance', title);
            show(title, 'success');
            push([myId], 'Event starting now!', title);
          }

          // Creator only: notify nearby users via in-app GUN notifications (no bulk push)
          if (myId === event.creatorId) {
            const users = allUsersRef.current;
            if (users?.length) {
              const nearbyUsers = users.filter(u => {
                if (!u.lat || !u.lng || u.nullifierHash === myId) return false;
                return distKm(u.lat, u.lng, event.lat, event.lng) <= 10;
              });
              nearbyUsers.forEach(u => {
                const notifId = `event_nearby_${event.id}_${u.nullifierHash}`;
                gun.get('joystance_notifications').get(u.nullifierHash).get(notifId).put({
                  ts: Date.now(),
                  fromNick: event.title,
                  text: '🎉 Happening nearby — just started!',
                  type: 'event',
                } as any);
              });
            }
          }

          saveRemindedId(event.id);
        }
      });
    };

    if (!checkedRef.current) { checkedRef.current = true; check(); }
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [events, myId, notify, push, show]);
}
